import { barChartLabelFormatter } from "@/common/utils/barChartLabelFormatter";
import { DashboardComponentChartProps } from "@/features/company/types/dashboards/dashboardComponent.type";
import { measureLegendWidth } from "@/features/company/utils/dashboards/measureLegendWidth";
import React, { useMemo, useState } from "react";
import { Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { ChartTooltip } from "./components/ChartTooltip";

const CHART_LEGEND_FONT_SIZE = 12;

const PieChartComponent = ({
  chartData,
  dashboardComponentUpdatedAt,
}: DashboardComponentChartProps) => {
  const nameKey = chartData?.primary_settings?.query_identifier;
  const seriesKeys = chartData?.series_settings ? Object.keys(chartData.series_settings) : [];

  const [chartWidth, setChartWidth] = useState(0);
  const [chartHeight, setChartHeight] = useState(0);

  const isVerticalByWidth = chartWidth <= 350;

  const parsedData = useMemo(() => {
    return chartData?.data?.map((entry) => {
      const row = { ...(entry as Record<string, unknown>) };

      seriesKeys.forEach((key) => {
        if (typeof row[key] === 'string') row[key] = parseFloat(row[key] as string);
      });

      row.fill = entry.color;

      return row;
    });
  }, [dashboardComponentUpdatedAt]);

  const legendWidth = useMemo(() => {
    if (isVerticalByWidth || !parsedData?.length) return 0;

    const labels = parsedData.map((row) => {
      const key = seriesKeys[0];
      const name = String(row[nameKey ?? ''] ?? '');

      const formatted = barChartLabelFormatter(
        row[key] as string ?? 0,
        chartData.series_settings[key].format_parser
      );

      return `${name} - ${formatted}`;
    });

    return measureLegendWidth(labels, CHART_LEGEND_FONT_SIZE);
  }, [dashboardComponentUpdatedAt, chartWidth, isVerticalByWidth]);

  const isVerticalLayout = useMemo(() => {
    if (isVerticalByWidth)
      return true;

    const outerRadius = Math.min(chartHeight, chartWidth) / 2 * 0.8;
    const leftEdge = chartWidth - 2 * outerRadius - 35 - legendWidth;

    return leftEdge < 0;
  }, [isVerticalByWidth, chartWidth, chartHeight, legendWidth]);

  const dynamicCx = useMemo(() => {
    if (isVerticalLayout) return '50%';

    return chartWidth - (Math.min(chartHeight, chartWidth) / 2 * 0.8) - 35 - legendWidth;
  }, [chartWidth, chartHeight, legendWidth]);

  const legendFormatters = useMemo(() => {
    if (!chartData?.series_settings || !parsedData) return {};

    return Object.fromEntries(
      Object.keys(chartData.series_settings).map((key) => [
        key,
        (value: string, _: any, index: number) => {
          return `${value} - ${barChartLabelFormatter(
            parsedData[index]?.[key] as string ?? 0,
            chartData.series_settings[key].format_parser,
          )}`
        }
      ]),
    );
  }, [parsedData, chartData]);

  return (
    <ResponsiveContainer
      width="100%"
      height="100%"
      onResize={(width, height) => {
        setChartWidth(width);
        setChartHeight(height);
      }}
    >
      <PieChart
        margin={{ right: isVerticalLayout ? 10 : 20, left: 10, top: 0, bottom: 10 }}
      >
        <Tooltip
          content={<ChartTooltip chartData={chartData} />}
        />

        {chartData?.series_settings && Object.entries(chartData.series_settings).map(([key]) => (
          <React.Fragment key={key}>
            <Pie
              data={parsedData}
              dataKey={key}
              nameKey={nameKey}
              stroke="#373c3d"
              strokeWidth={0}
              isAnimationActive
              cx={dynamicCx}
              cy="50%"
              outerRadius={isVerticalLayout ? '80%' : Math.min(chartHeight, chartWidth) / 2 * 0.8}
            />

            <Legend
              iconType="circle"
              iconSize={12}
              wrapperStyle={{ fontSize: CHART_LEGEND_FONT_SIZE }}
              labelStyle={{ color: '#ffffff' }}
              layout="vertical"
              align={isVerticalLayout ? 'center' : 'left'}
              verticalAlign={isVerticalLayout ? 'bottom' : 'top'}
              formatter={legendFormatters[key]}
            />
          </React.Fragment>
        ))}
      </PieChart>
    </ResponsiveContainer>
  );
};

export default PieChartComponent;
